import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import './styles/main.css';

// Import pages and components
import Home from './pages/Home';
import EventDetail from './pages/EventDetail';
import CallForAbstracts from './pages/CallForAbstracts';
import Contact from './pages/Contact';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import ProtectedAdminRoute from './components/admin/ProtectedAdminRoute';
import ContentManagement from './components/admin/ContentManagement/ContentManagement';
import UserManagement from './components/admin/UserManagement/UserManagement';
import SpeakerList from './components/admin/SpeakerManagement/SpeakerList';
import SpeakerDetail from './components/admin/SpeakerManagement/SpeakerDetail';
import SpeakerResearch from './components/admin/SpeakerManagement/SpeakerResearch';
import SpeakerDetection from './components/admin/SpeakerManagement/SpeakerDetection';
import ResetPassword  from './components/admin/ResetPassword';
import SpeakerLogin from './components/speaker/SpeakerLogin';
import SpeakerDashboard from './components/speaker/SpeakerDashboard';
import ProtectedSpeakerRoute from './components/speaker/ProtectedSpeakerRoute';
import SpeakerInvitationLanding from './components/speaker/SpeakerInvitationLanding';
import SpeakerInviteConfirmation from './components/speaker/SpeakerInviteConfirmation';

import AttendeeLogin from './components/attendee/AttendeeLogin';
import AttendeeSignup from './components/attendee/AttendeeSignup';
import AttendeeProfile from './components/attendee/AttendeeProfile';
import ProtectedAttendeeRoute from './components/attendee/ProtectedAttendeeRoute';
import VerifyEmail from './components/attendee/VerifyEmail';
import AttendeeEventRegisterConfirm from './components/AttendeeEventRegisterConfirm';

import SubmissionsPage from './components/submissions/SubmissionsPage';
import SubmissionList from './components/admin/SubmissionManagement/SubmissionList';

import Header from './layouts/Header';
import Footer from './layouts/Footer';
import Modal from './components/Modal';
import ImpressumDisplay from './components/ImpressumDisplay';
import ScrollToTop from './components/ScrollToTop';

import CookieConsent from './components/CookieConsent';
import MatomoTracker from './components/MatomoTracker';

// Import language files
import de from './locales/de.json';
import en from './locales/en.json';

// Import the ConferenceProvider
import { ConferenceProvider } from './contexts/ConferenceContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { getRole } from './utils/authUtils';

const messages = {
  de: de,
  en: en,
};

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(to bottom, #CAD4DD, #060d25);
`;

const ContentWrapper = styled.main`
  flex: 1;
`;

function AppContent() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  console.log('selectedEvent: ', selectedEvent)
  const openModal = (eventInfo) => {
    // Attending is now login-gated: an anonymous visitor is sent to sign up
    // (or log in) instead of seeing the registration modal. The event they
    // meant to register for travels along as the ?eventId= query param -
    // AttendeeSignup/AttendeeLogin/AttendeeAccountService pick it up from
    // there, so it completes automatically once they're authenticated.
    if (!getRole()) {
      navigate(eventInfo?.id ? `/signup?eventId=${eventInfo.id}` : '/signup');
      return;
    }
    setSelectedEvent(eventInfo || {});
    setModalOpen(true);
    setRegistrationSuccess(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    setRegistrationSuccess(false);
  };

  const handleRegistrationSuccess = () => {
    setRegistrationSuccess(true);
    setTimeout(() => {
      closeModal();
    }, 3000);
  };

  const getContent = (defaultContent, enContent) => {
    console.log('selectedEvent 2: ', selectedEvent)

    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  return (
    <IntlProvider messages={messages[language]} locale={language}>
      <AppWrapper>
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={
            registrationSuccess
              ? <FormattedMessage id="registration.success" defaultMessage="Registration Successful" />
              : <>{getContent(selectedEvent?.mainHeadline, selectedEvent?.mainHeadlineEN) || ''}</>
            }
        >
          <AttendeeEventRegisterConfirm
            onSubmitSuccess={handleRegistrationSuccess}
            eventInfo={selectedEvent || {}}
            registrationSuccess={registrationSuccess}
          />
        </Modal>
        <Header />
        <ScrollToTop />
        <ContentWrapper>
          <Routes>
            <Route path="/" element={<Home openRegistrationModal={openModal} />} />
            <Route path="/events/:slug" element={<EventDetail openRegistrationModal={openModal} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>} />
            <Route path="/admin/user-management" element={<ProtectedAdminRoute><UserManagement /></ProtectedAdminRoute>} />
            <Route path="/admin/content-management" element={<ProtectedAdminRoute><ContentManagement /></ProtectedAdminRoute>} />
            <Route path="/admin/speaker-management" element={<ProtectedAdminRoute><SpeakerList /></ProtectedAdminRoute>} />
            <Route path="/admin/speaker-management/new" element={<ProtectedAdminRoute><SpeakerResearch /></ProtectedAdminRoute>} />
            <Route path="/admin/speaker-management/detect" element={<ProtectedAdminRoute><SpeakerDetection /></ProtectedAdminRoute>} />
            <Route path="/admin/speaker-management/:id" element={<ProtectedAdminRoute><SpeakerDetail /></ProtectedAdminRoute>} />
            <Route path="/admin/submission-management" element={<ProtectedAdminRoute><SubmissionList /></ProtectedAdminRoute>} />
            <Route path="/speaker/login" element={<SpeakerLogin />} />
            <Route path="/speaker/invite" element={<SpeakerInvitationLanding />} />
            <Route path="/speaker/invite/confirmed" element={<ProtectedAttendeeRoute><SpeakerInviteConfirmation /></ProtectedAttendeeRoute>} />
            <Route path="/speaker/dashboard" element={<ProtectedSpeakerRoute><SpeakerDashboard /></ProtectedSpeakerRoute>} />
            <Route path="/login" element={<AttendeeLogin />} />
            <Route path="/signup" element={<AttendeeSignup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/profile" element={<ProtectedAttendeeRoute><AttendeeProfile /></ProtectedAttendeeRoute>} />
            {/* ProtectedAttendeeRoute already accepts ANY authenticated role (ATTENDEE/SPEAKER/ADMIN) -
                exactly the access submissions need, so it's reused as-is rather than adding a new guard. */}
            <Route path="/call-for-abstracts" element={<ProtectedAttendeeRoute><CallForAbstracts /></ProtectedAttendeeRoute>} />
            <Route path="/submissions" element={<ProtectedAttendeeRoute><SubmissionsPage /></ProtectedAttendeeRoute>} />
            <Route path="/uploads/*" element={null} />
            <Route path="/impressum" element={<ImpressumDisplay />} />
            <Route path="/reset-password" element={<ResetPassword />} />


          </Routes>
        </ContentWrapper>
        <Footer />
      </AppWrapper>
    </IntlProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ConferenceProvider>
        <Router>
          <MatomoTracker />
          <CookieConsent />
          <AppContent />
        </Router>
      </ConferenceProvider>
    </LanguageProvider>
  );
}

export default App;
